import axios from 'axios'
import type { AxiosInstance, AxiosError } from 'axios'
import { supabase } from './supabase'
import { toast } from 'sonner'

// Use environment variable or fallback (HTTPS for production)
let API_URL = import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD ? 'https://admin-api.joulescrm.com/api/v1' : 'http://localhost:8000/api/v1')

// Force HTTPS in production for admin-api.joulescrm.com
if (import.meta.env.PROD && API_URL.includes('admin-api.joulescrm.com') && API_URL.startsWith('http://')) {
  API_URL = API_URL.replace('http://', 'https://')
  console.warn('[API Service] Forced HTTP to HTTPS for production:', API_URL)
}

// Debug logging for production issues (temporary)
if (import.meta.env.MODE === 'development') {
  console.log('[API Service] Environment variables:')
  console.log('  VITE_API_URL:', import.meta.env.VITE_API_URL)
  console.log('  Final API_URL:', API_URL)
}

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Long-running endpoints that should have extended timeouts and no timeout toasts
const LONG_RUNNING_ENDPOINTS = [
  '/blog/ai-writer/posts',
  '/release-posts/generate'
]

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    // Add trailing slash only to specific collection endpoints that need them
    // These are endpoints defined with @router.get("/") in FastAPI
    const endpointsNeedingTrailingSlash = ['/tenants', '/users', '/billing', '/support']

    if (config.url && !config.url.endsWith('/')) {
      const [urlPath] = config.url.split('?')
      // Only add trailing slash to exact matches or root collection endpoints
      const needsTrailingSlash = endpointsNeedingTrailingSlash.includes(urlPath)

      if (needsTrailingSlash) {
        const [urlPath, queryParams] = config.url.split('?')
        config.url = urlPath + '/' + (queryParams ? `?${queryParams}` : '')
      }
    }

    // Temporary fix for analytics endpoint redirect issue
    if (config.url === '/analytics/overview') {
      // Force HTTPS and ensure no redirect by using exact backend URL format
      config.url = '/analytics/overview'
      // Override baseURL temporarily to force correct protocol
      if (config.baseURL?.startsWith('http://')) {
        config.baseURL = config.baseURL.replace('http://', 'https://')
      }
    }

    // Extend timeout for long-running AI operations (5 minutes)
    if (config.url && LONG_RUNNING_ENDPOINTS.some(endpoint => config.url?.includes(endpoint))) {
      config.timeout = 300000 // 5 minutes
      // Mark request as long-running so we can suppress timeout toast
      config.headers['X-Long-Running'] = 'true'
    }

    // Debug logging in development only
    if (import.meta.env.MODE === 'development') {
      console.log('[API Request]', config.url)
    }

    // Get the current session token
    const { data: { session } } = await supabase.auth.getSession()

    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: Error | null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve()
    }
  })
  failedQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean }

    if (import.meta.env.MODE === 'development') {
      console.log('[API Error]', error.config?.url, error.response?.status)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(() => {
          return api(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession()

        if (refreshError || !session) {
          throw refreshError || new Error('Failed to refresh session')
        }

        processQueue(null)
        isRefreshing = false

        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError as Error)
        isRefreshing = false

        toast.error('Your session has expired. Please log in again.')
        await supabase.auth.signOut()
        window.location.href = '/login'

        return Promise.reject(refreshError)
      }
    }

    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      // Don't show timeout toast for long-running AI operations - they're expected to take time
      const isLongRunning = originalRequest?.headers?.['X-Long-Running'] === 'true'
      if (!isLongRunning) {
        toast.error('Request timed out. Please check your connection and try again.')
      }
    }

    return Promise.reject(error)
  }
)

// API service methods
export const apiService = {
  // Generic methods
  get: <T = unknown>(url: string, params?: Record<string, unknown>) => api.get<T>(url, { params }).then(res => res.data),
  post: <T = unknown>(url: string, data?: Record<string, unknown>) => api.post<T>(url, data).then(res => res.data),
  put: <T = unknown>(url: string, data?: Record<string, unknown>) => api.put<T>(url, data).then(res => res.data),
  delete: <T = unknown>(url: string) => api.delete<T>(url).then(res => res.data),

  // Tenant management
  tenants: {
    list: () => api.get('/tenants').then(res => res.data),
    get: (id: string) => api.get(`/tenants/${id}`).then(res => res.data),
    create: (data: Record<string, unknown>) => api.post('/tenants', data).then(res => res.data),
    update: (id: string, data: Record<string, unknown>) => api.put(`/tenants/${id}`, data).then(res => res.data),
    delete: (id: string) => api.delete(`/tenants/${id}`).then(res => res.data),
    suspend: (id: string) => api.post(`/tenants/${id}/suspend`).then(res => res.data),
    activate: (id: string) => api.post(`/tenants/${id}/activate`).then(res => res.data),
  },

  // Analytics
  analytics: {
    dashboard: () => api.get('/analytics/dashboard').then(res => res.data),
    tenantMetrics: (tenantId: string) => api.get(`/analytics/tenants/${tenantId}`).then(res => res.data),
    revenue: (params?: Record<string, unknown>) => api.get('/analytics/revenue', { params }).then(res => res.data),
    usage: (params?: Record<string, unknown>) => api.get('/analytics/usage', { params }).then(res => res.data),
  },

  // Billing lives in src/services/billing.ts (billingService)

  // User management
  users: {
    list: (params?: Record<string, unknown>) => api.get('/users/', { params }).then(res => res.data),
    stats: () => api.get('/users/stats/').then(res => res.data),
    get: (id: string) => api.get(`/users/${id}/`).then(res => res.data),
    update: (id: string, data: Record<string, unknown>) => api.put(`/users/${id}/`, data).then(res => res.data),
    suspend: (id: string) => api.post(`/users/${id}/suspend/`).then(res => res.data),
    activate: (id: string) => api.post(`/users/${id}/activate/`).then(res => res.data),
    impersonate: (id: string) => api.post(`/users/${id}/impersonate/`).then(res => res.data),
  },

  // Support
  support: {
    tickets: (params?: Record<string, unknown>) => api.get('/support/tickets', { params }).then(res => res.data),
    getTicket: (id: string) => api.get(`/support/tickets/${id}`).then(res => res.data),
    updateTicket: (id: string, data: Record<string, unknown>) => api.put(`/support/tickets/${id}`, data).then(res => res.data),
    logs: (tenantId: string, params?: Record<string, unknown>) => api.get(`/support/tenants/${tenantId}/logs`, { params }).then(res => res.data),
  },

  // Credits management
  credits: {
    add: (data: { tenant_id: string; amount: number; description?: string; metadata?: Record<string, unknown> }) =>
      api.post('/credits/add', data).then(res => res.data),
    getBalance: (tenantId: string) =>
      api.get(`/credits/balance/${tenantId}`).then(res => res.data),
    getTransactions: (tenantId: string, limit?: number) =>
      api.get(`/credits/transactions/${tenantId}`, { params: { limit } }).then(res => res.data),
  },

  // Blog management (Admin)
  blog: {
    // Blog posts
    posts: {
      list: (params?: Record<string, unknown>) => api.get('/blog/posts/', { params }).then(res => res.data),
      get: (id: string) => api.get(`/blog/posts/${id}`).then(res => res.data),
      create: (data: Record<string, unknown>) => api.post('/blog/posts/', data).then(res => res.data),
      update: (id: string, data: Record<string, unknown>) => api.put(`/blog/posts/${id}`, data).then(res => res.data),
      delete: (id: string) => api.delete(`/blog/posts/${id}`).then(res => res.data),
      publish: (id: string) => api.post(`/blog/posts/${id}/publish`).then(res => res.data),
      unpublish: (id: string) => api.post(`/blog/posts/${id}/unpublish`).then(res => res.data),
      schedule: (id: string, data: Record<string, unknown>) => api.post(`/blog/posts/${id}/schedule`, data).then(res => res.data),
    },
    // Revision management
    revisions: {
      list: (postId: string, params?: Record<string, unknown>) =>
        api.get(`/blog/posts/${postId}/revisions`, { params }).then(res => res.data),
      get: (postId: string, revisionId: string) =>
        api.get(`/blog/posts/${postId}/revisions/${revisionId}`).then(res => res.data),
      create: (postId: string, data: { revision_notes?: string; is_autosave?: boolean }) =>
        api.post(`/blog/posts/${postId}/revisions`, data).then(res => res.data),
      restore: (postId: string, revisionId: string) =>
        api.post(`/blog/posts/${postId}/restore/${revisionId}`).then(res => res.data),
      diff: (postId: string, revisionId: string) =>
        api.get(`/blog/posts/${postId}/diff/${revisionId}`).then(res => res.data),
    },
    // Lock management
    locks: {
      acquire: (postId: string) =>
        api.post(`/blog/posts/${postId}/lock`).then(res => res.data),
      release: (postId: string, lockToken: string) =>
        api.delete(`/blog/posts/${postId}/lock`, { params: { lock_token: lockToken } }).then(res => res.data),
      check: (postId: string) =>
        api.get(`/blog/posts/${postId}/lock`).then(res => res.data),
    },
    // Conflict detection
    conflicts: {
      check: (postId: string, data: { current_version: number; last_modified_at: string }) =>
        api.post(`/blog/posts/${postId}/check-conflict`, data).then(res => res.data),
    },
    // Categories
    categories: {
      list: () => api.get('/blog/categories/').then(res => res.data),
      get: (id: string) => api.get(`/blog/categories/${id}`).then(res => res.data),
      create: (data: Record<string, unknown>) => api.post('/blog/categories/', data).then(res => res.data),
      update: (id: string, data: Record<string, unknown>) => api.put(`/blog/categories/${id}`, data).then(res => res.data),
      delete: (id: string) => api.delete(`/blog/categories/${id}`).then(res => res.data),
    },
    // Tags
    tags: {
      list: () => api.get('/blog/tags/').then(res => res.data),
      get: (id: string) => api.get(`/blog/tags/${id}`).then(res => res.data),
      create: (data: Record<string, unknown>) => api.post('/blog/tags/', data).then(res => res.data),
      update: (id: string, data: Record<string, unknown>) => api.put(`/blog/tags/${id}`, data).then(res => res.data),
      delete: (id: string) => api.delete(`/blog/tags/${id}`).then(res => res.data),
    },
    // Image Management
    images: {
      upload: (formData: FormData) =>
        api.post('/blog/images/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => res.data),
      uploadMultiple: (formData: FormData) =>
        api.post('/blog/images/upload-multiple', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => res.data),
      delete: (id: string) =>
        api.delete(`/blog/images/${id}`).then(res => res.data),
      gallery: (params?: {
        search?: string
        folder?: string
        has_usage?: boolean
        limit?: number
        offset?: number
      }) => api.get('/blog/images/gallery', { params }).then(res => res.data),
      getUsage: (imageId: string) =>
        api.get(`/blog/images/${imageId}/usage`).then(res => res.data),
      // Bulk Operations (Story 12 - AC-24, Tasks 3.14-3.16)
      bulkDelete: (data: { image_ids: string[] }) =>
        api.post('/blog/images/bulk/delete', data).then(res => res.data),
      unused: (params?: { limit?: number; offset?: number }) =>
        api.get('/blog/images/unused', { params }).then(res => res.data),
      optimizationRecommendations: () =>
        api.get('/blog/images/optimization-recommendations').then(res => res.data),
    },
    // Blog Analytics (Story 10)
    analytics: {
      overview: () => api.get('/blog/analytics/overview').then(res => res.data),
      posts: (params?: {
        sort_by?: 'views' | 'title' | 'published_at'
        order?: 'asc' | 'desc'
        status?: string
        limit?: number
        offset?: number
      }) => api.get('/blog/analytics/posts', { params }).then(res => res.data),
      performance: (params?: {
        period?: '7days' | '30days' | '90days' | 'all'
      }) => api.get('/blog/analytics/performance', { params }).then(res => res.data),
      export: (params?: {
        format?: 'csv' | 'json'
      }) => api.get('/blog/analytics/export', {
        params,
        responseType: 'blob'
      }).then(res => res.data),
    },
    // Bulk Operations (Story 12)
    bulk: {
      updateStatus: (data: { post_ids: string[], new_status: string }) =>
        api.post('/blog/posts/bulk/status', data).then(res => res.data),
      publish: (data: { post_ids: string[] }) =>
        api.post('/blog/posts/bulk/publish', data).then(res => res.data),
      unpublish: (data: { post_ids: string[] }) =>
        api.post('/blog/posts/bulk/unpublish', data).then(res => res.data),
      delete: (data: { post_ids: string[] }) =>
        api.post('/blog/posts/bulk/delete', data).then(res => res.data),
      assignCategories: (data: { post_ids: string[], category_ids: string[], replace?: boolean }) =>
        api.post('/blog/posts/bulk/categories', data).then(res => res.data),
      assignTags: (data: { post_ids: string[], tag_ids: string[], replace?: boolean }) =>
        api.post('/blog/posts/bulk/tags', data).then(res => res.data),
    },
    // Content Templates (Story 12 - AC-19)
    templates: {
      list: () => api.get('/blog/templates').then(res => res.data),
      get: (id: string) => api.get(`/blog/templates/${id}`).then(res => res.data),
      create: (data: {
        name: string
        description?: string
        title_template?: string
        content_template: string
        excerpt_template?: string
        category_ids?: string[]
        tag_ids?: string[]
      }) => api.post('/blog/templates', data).then(res => res.data),
      update: (id: string, data: {
        name?: string
        description?: string
        title_template?: string
        content_template?: string
        excerpt_template?: string
        category_ids?: string[]
        tag_ids?: string[]
      }) => api.put(`/blog/templates/${id}`, data).then(res => res.data),
      delete: (id: string) => api.delete(`/blog/templates/${id}`).then(res => res.data),
    },
    // Content Snippets (Story 12 - AC-19)
    snippets: {
      list: () => api.get('/blog/snippets').then(res => res.data),
      get: (id: string) => api.get(`/blog/snippets/${id}`).then(res => res.data),
      create: (data: {
        name: string
        description?: string
        content: string
      }) => api.post('/blog/snippets', data).then(res => res.data),
      update: (id: string, data: {
        name?: string
        description?: string
        content?: string
      }) => api.put(`/blog/snippets/${id}`, data).then(res => res.data),
      delete: (id: string) => api.delete(`/blog/snippets/${id}`).then(res => res.data),
    },
    // AI Blog Writer (Story BLOG-AI-002, BLOG-AI-003, BLOG-AI-004)
    aiWriter: {
      // Get next question in the flow
      getNextQuestion: (data: { current_answers: Array<{ question_id: string; answer: string }> }) =>
        api.post('/blog/ai-writer/next-question', data).then(res => res.data),
      // Create AI-generated blog post
      createPost: (data: { ai_mode: true; ai_answers: Record<string, string> }) =>
        api.post('/blog/ai-writer/posts', data).then(res => res.data),
      // Headline approval/revision
      approveHeadline: (postId: string, data: { selected_headline: string }) =>
        api.post(`/blog/posts/${postId}/approve-headline`, data).then(res => res.data),
      reviseHeadlines: (postId: string, data: { feedback: string }) =>
        api.post(`/blog/posts/${postId}/revise-headlines`, data).then(res => res.data),
      // Outline approval/revision
      approveOutline: (postId: string) =>
        api.post(`/blog/posts/${postId}/approve-outline`).then(res => res.data),
      reviseOutline: (postId: string, data: { feedback: string }) =>
        api.post(`/blog/posts/${postId}/revise-outline`, data).then(res => res.data),
      // Length decision (Story BLOG-AI-006)
      lengthDecision: (postId: string, data: { decision: 'continue' | 'trim' | 'split_series' }) =>
        api.post(`/blog/posts/${postId}/length-decision`, data).then(res => res.data),
      // Analytics (Story BLOG-AI-004)
      getAnalytics: (params?: { start_date?: string; end_date?: string }) =>
        api.get('/blog/ai-writer/analytics', { params }).then(res => res.data),
      // Cancel AI generation (Story UX-003)
      cancel: (postId: string) =>
        api.post(`/blog/posts/${postId}/cancel`).then(res => res.data),
    },
  },

  // Release Post Generator (Story RELEASE-002 - Multi-Video Support)
  releasePosts: {
    // Extract Loom transcript (preview only) - kept for backward compatibility
    extractLoom: (data: { loom_url: string }) =>
      api.post('/release-posts/extract-loom', data).then(res => res.data),
    // Generate complete release post (background task) - now supports multiple videos
    generate: (data: {
      post_id: string
      videos: Array<{
        loom_url?: string
        transcript: string
        title?: string
        description?: string
        input_method: 'auto_extract' | 'manual_paste'
      }>
      release_version?: string // Optional - backend auto-generates if not provided
      release_date: string
      release_type: 'major' | 'minor' | 'patch' | 'feature'
      target_audience: 'all_users' | 'admins' | 'developers'
      feature_summary?: string
      keywords?: string
    }) => api.post('/release-posts/generate', data).then(res => res.data),
    // Check generation status
    getStatus: (postId: string) =>
      api.get(`/release-posts/${postId}/status`).then(res => res.data),
    // Cancel release post generation (Story UX-003)
    cancel: (postId: string) =>
      api.post(`/blog/posts/${postId}/cancel`).then(res => res.data),
  },
}

// Export as apiClient for backward compatibility
export const apiClient = {
  get: (url: string, options?: Record<string, unknown>) => 
    api.get(url, options),
  post: (url: string, data?: Record<string, unknown>, options?: Record<string, unknown>) => 
    api.post(url, data, options),
  put: (url: string, data?: Record<string, unknown>, options?: Record<string, unknown>) => 
    api.put(url, data, options),
  delete: (url: string, options?: Record<string, unknown>) => 
    api.delete(url, options),
}

export default api